import { Text } from 'tradera/components/atoms/text/text';
import { classNames } from 'tradera/utils/class-names';
import { Popover } from 'tradera/components/popover/popover';
import { useMemberServerStorageBoolean } from 'tradera/state/member-storage/hooks';
import { useTranslator } from 'tradera/lang/translate';
import UserIcon from '@tradera/blueprint/theme/icons2/user.svg?jsx';
import styles from './profile.module.scss';

const NAVBAR_POPOVER_KEY = 'has_seen_navbar_popover';

type ProfileProps = {
    isLoggedIn: boolean;
    facebookId: string | null;
    profilePicture: string | null;
    firstLetter?: string;
    showTooltip?: boolean;
};

const useShowNavbarPopover = () => {
    const [hasSeenPopover, setHasSeenPopover, { isReady }] =
        useMemberServerStorageBoolean(NAVBAR_POPOVER_KEY, false);

    const handlePopoverClose = () => {
        setHasSeenPopover(true);
    };

    return {
        shouldShowPopover: !hasSeenPopover && isReady,
        handlePopoverClose,
    };
};

export const Profile = ({
    isLoggedIn,
    facebookId,
    profilePicture,
    firstLetter,
    showTooltip,
}: ProfileProps) => {
    const { t } = useTranslator();
    const { shouldShowPopover, handlePopoverClose } = useShowNavbarPopover();

    const renderProfileContent = () => {
        if (isLoggedIn && profilePicture) {
            return (
                <img
                    alt="Profile"
                    width="21"
                    height="21"
                    src={profilePicture}
                />
            );
        }

        if (isLoggedIn && facebookId) {
            return (
                <img
                    alt="Profile"
                    width="21"
                    height="21"
                    src={`https://graph.facebook.com/v17.0/${facebookId}/picture?type=square&height=48&width=48`}
                />
            );
        }

        return isLoggedIn ? (
            <div
                className={classNames(
                    'd-flex align-items-center justify-content-center',
                    styles.noImage,
                )}>
                <Text
                    color="primary"
                    fontWeight="heavy"
                    font="hansen"
                    className="size-oslo text-uppercase">
                    {firstLetter}
                </Text>
            </div>
        ) : (
            <UserIcon className="icon icon-md" />
        );
    };

    if (showTooltip && shouldShowPopover) {
        return (
            <Popover
                title={t('bottomNav_popover_title')}
                text={t('bottomNav_popover_text')}
                buttonText={t('bottomNav_popover_button')}
                onClick={handlePopoverClose}
                onClose={handlePopoverClose}
                defaultOpen={true}>
                {renderProfileContent()}
            </Popover>
        );
    }

    return renderProfileContent();
};
